import Choices from 'choices.js';
import Chart from 'chart.js/auto';

const chart = {
    CONSTANTS: {
        FONT_FAMILY: 'Helvetica Now Display Fallback, Arial, sans-serif',
        ORANGE_COLOR: '#F98800',
        GRID_COLOR: 'rgba(200, 200, 200, 0.3)',
        TEXT_COLOR: '#81848A',
        TOOLTIP_BACKGROUND: '#fff',
        TOOLTIP_TITLE_COLOR: '#212226',
        ACCENT_COLOR: '#14217E',
        CACHE_DURATION: 300000,
        MAX_CACHE_SIZE: 10,
        DEBOUNCE_DELAY: 250,
    },
    
    settings: {
        container: document.querySelector('.js-bitcoin-price'),
        canvas: document.querySelector('.js-chart'),
        selects: document.querySelectorAll('.js-chart-select'),
        valueDisplay: document.querySelector('.js-chart-value'),
        percentageDisplay: document.querySelector('.js-chart-percentage'),
        percentageValueDisplay: document.querySelector('.js-chart-percentage-value'),
        chartInstance: null,
        defaultCurrency: document.querySelector('.js-curr-select')?.value || 'EUR',
        selectedCurrency: document.querySelector('.js-curr-select')?.value || 'EUR',
        selectedPeriod: 'yearly',
        radioInputs: document.querySelectorAll('.js-chart-type'),
        dataCache: {},
        eventListeners: [],
        abortController: null,
        choicesInstances: [],
    },

    init() {
        if (!this.settings.canvas) return;

        this.initSelect();
        this.setEvents();
        this.drawPriceHistoryChart();
        this.listenForPriceUpdates();
    },

    destroy() {
        this.settings.eventListeners.forEach(({ element, event, handler }) => {
            element.removeEventListener(event, handler);
        });
        this.settings.eventListeners = [];
        
        if (this.settings.chartInstance) {
            this.settings.chartInstance.destroy();
            this.settings.chartInstance = null;
        }

        if (this.settings.abortController) {
            this.settings.abortController.abort();
        }

        if (this.settings.choicesInstances) {
            this.settings.choicesInstances.forEach(instance => {
                if (instance && typeof instance.destroy === 'function') {
                    instance.destroy();
                }
            });
            this.settings.choicesInstances = [];
        }
    },

    debounce(func, wait) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    },
    
    isMobile() {
        return window.matchMedia('(max-width: 768px)').matches;
    },

    initSelect() {
        this.settings.selects.forEach(select => {
            const choicesInstance = new Choices(select, {
                searchEnabled: false,
                itemSelectText: '',
                shouldSort: false,
                classNames: {
                    selectedState: ['is-selected'],
                }
            });
            this.settings.choicesInstances.push(choicesInstance);
        });
    },

    setEvents() {
        const currencySelect = document.querySelector('.js-curr-select');
        
        if (currencySelect) {
            const currencyHandler = (e) => {
                this.settings.selectedCurrency = e.target.value;
                this.drawPriceHistoryChart();
            };
            
            currencySelect.addEventListener('change', currencyHandler);
            this.settings.eventListeners.push({
                element: currencySelect,
                event: 'change',
                handler: currencyHandler
            });
        }

        const resizeHandler = this.debounce(() => {
            this.updateChartResponsively();
        }, this.CONSTANTS.DEBOUNCE_DELAY);
        
        window.addEventListener('resize', resizeHandler);
        this.settings.eventListeners.push({
            element: window,
            event: 'resize',
            handler: resizeHandler
        });

        if (this.settings.radioInputs) {
            this.settings.radioInputs.forEach(radio => {
                const radioHandler = async (e) => {
                    if (e.target.checked) {
                        this.settings.selectedPeriod = e.target.value;
                        await this.drawPriceHistoryChart();
                        // Re-apply last bitcoin price update after period change
                        if (window.lastBitcoinPriceUpdate) {
                            this.handlePriceUpdate(window.lastBitcoinPriceUpdate);
                        }
                    }
                };
                radio.addEventListener('change', radioHandler);
                this.settings.eventListeners.push({
                    element: radio,
                    event: 'change',
                    handler: radioHandler
                });

                // Prevent page jump - scroll to container instead
                const focusHandler = () => {
                    if (this.settings.container) {
                        this.settings.container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                };
                radio.addEventListener('focus', focusHandler);
                this.settings.eventListeners.push({
                    element: radio,
                    event: 'focus',
                    handler: focusHandler
                });
            });
        }

        // Also handle select focus
        if (currencySelect) {
            const selectFocusHandler = () => {
                if (this.settings.container) {
                    this.settings.container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            };
            currencySelect.addEventListener('focus', selectFocusHandler);
            this.settings.eventListeners.push({
                element: currencySelect,
                event: 'focus',
                handler: selectFocusHandler
            });
        }
    },
    
    getResponsiveTickLimits() {
        const isMobile = this.isMobile();
        return {
            x: isMobile ? 4 : 6,
            y: isMobile ? 4 : 3,
        };
    },

    updateChartResponsively() {
        if (!this.settings.chartInstance) return;
        
        const isMobile = this.isMobile();
        const prevMobile = this._prevIsMobile;
        this._prevIsMobile = isMobile;
        
        const tickLimits = this.getResponsiveTickLimits();
        const currency = this.settings.selectedCurrency || this.settings.defaultCurrency;
        
        this.settings.chartInstance.data.datasets[0].backgroundColor = this.createChartGradient();
        this.settings.chartInstance.options.scales.x.ticks.maxTicksLimit = tickLimits.x;
        this.settings.chartInstance.options.scales.y.ticks.maxTicksLimit = tickLimits.y;
        
        this.settings.chartInstance.options.scales.y.ticks.callback = (value, index) => {
            if (isMobile && index === 0) {
                return '';
            }
            return this.formatCurrency(value, currency, true);
        };
        
        if (prevMobile !== isMobile) {
            this.settings.chartInstance.options.scales.y.position = 'left';
            this.settings.chartInstance.options.scales.y.offset = false;
            this.settings.chartInstance.options.scales.y.ticks.mirror = isMobile;
            this.settings.chartInstance.options.scales.y.ticks.z = 1;
        }
        
        this.settings.chartInstance.update('none');
    },

    calculatePercentageChange(history, period) {
        if (!history || history.length < 2) return 0;
        
        const sortedHistory = [...history].sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
        
        const currentValue = sortedHistory[sortedHistory.length - 1].value;
        const currentTime = new Date(sortedHistory[sortedHistory.length - 1].datetime);
        
        let comparisonValue = null;
        
        // For daily view, compare to first value in displayed data (midnight)
        // This matches what's shown on the chart since data is filtered from midnight
        if (period === 'daily') {
            comparisonValue = sortedHistory[0].value;
        } else {
            const comparisonDate = new Date(currentTime);
            
            if (period === 'weekly') {
                comparisonDate.setDate(comparisonDate.getDate() - 7);
            } else if (period === 'monthly') {
                comparisonDate.setMonth(comparisonDate.getMonth() - 1);
            } else if (period === 'yearly') {
                comparisonDate.setFullYear(comparisonDate.getFullYear() - 1);
            } else if (period === '5years') {
                comparisonDate.setFullYear(comparisonDate.getFullYear() - 5);
            }
            
            comparisonValue = this.findClosestValueByDate(sortedHistory, comparisonDate);
            
            if (comparisonValue === null && sortedHistory.length > 0) {
                comparisonValue = sortedHistory[0].value;
            }
        }
        
        if (comparisonValue && currentValue) {
            return ((currentValue - comparisonValue) / comparisonValue) * 100;
        }
        
        return 0;
    },
    
    findClosestValueByDate(history, targetDate) {
        if (!history || !history.length) return null;
        
        let closestEntry = null;
        let minTimeDiff = Infinity;
        
        for (const entry of history) {
            const entryDate = new Date(entry.datetime);
            const timeDiff = Math.abs(entryDate.getTime() - targetDate.getTime());
            
            if (timeDiff < minTimeDiff) {
                minTimeDiff = timeDiff;
                closestEntry = entry;
            }
        }
        
        return closestEntry ? closestEntry.value : null;
    },

    calculateHoverPercentage(dataIndex, currency, period) {
        const cacheKey = `${currency}-${period}`;
        const cachedData = this.settings.dataCache[cacheKey]?.data;
        
        if (!cachedData || !cachedData.rawHistory) return null;
        
        const rawHistory = cachedData.rawHistory;
        
        if (dataIndex >= 0 && dataIndex < rawHistory.length) {
            const hoveredPoint = rawHistory[dataIndex];
            
            const historySubset = rawHistory.filter(item => 
                new Date(item.datetime) <= new Date(hoveredPoint.datetime)
            );
            
            if (historySubset.length > 0) {
                return this.calculatePercentageChange(historySubset, period);
            }
        }
        
        return null;
    },

    updateHoverPercentage(dataIndex, currency, period) {
        if (dataIndex !== null) {
            const pointPercentage = this.calculateHoverPercentage(dataIndex, currency, period);

            if (pointPercentage !== null) {
                this.settings.percentageDisplay.classList.remove('is-hidden');
                this.updatePercentageDisplay(pointPercentage);
            }

        } else {
            const cacheKey = `${currency}-${period}`;
            const cachedData = this.settings.dataCache[cacheKey]?.data;
            
            if (cachedData && cachedData.rawHistory) {
                const percentageChange = this.calculatePercentageChange(cachedData.rawHistory, period);
                this.settings.percentageDisplay.classList.add('is-hidden');
                this.updatePercentageDisplay(percentageChange);
            }
        }
    },

    setValueOnHover() {
        const chartObj = this;
        return {
            id: 'updateValueOnHover',
            afterEvent(chart, args) {
                const tooltip = chart.tooltip;
                const valueDisplay = chartObj.settings.valueDisplay;
                const period = chartObj.settings.selectedPeriod;
                const currency = chartObj.settings.selectedCurrency || chartObj.settings.defaultCurrency;
                const event = args.event;
                
                if (!valueDisplay) return;

                if (event.type === 'mouseleave' || event.type === 'mouseout') {
                    const livePrice = window.lastBitcoinPriceUpdate?.prices?.[currency]?.value ?? null;
                    const cacheKey = `${currency}-${period}`;
                    const currentPrice = chartObj.settings.dataCache[cacheKey]?.data?.currentPrice ?? null;
                    chartObj.updateLastValue(
                        currency,
                        chart.data.datasets[0].data,
                        livePrice ?? currentPrice
                    );
                    chartObj.settings.percentageDisplay.classList.add('is-hidden');
                    return;
                }

                if (tooltip && tooltip.dataPoints && tooltip.dataPoints.length) {
                    const value = tooltip.dataPoints[0].parsed.y;
                    const dataIndex = tooltip.dataPoints[0].dataIndex;
                    
                    valueDisplay.classList.remove('is-hidden');
                    valueDisplay.textContent = chartObj.formatCurrency(value, currency);
                    chartObj.updateHoverPercentage(dataIndex, currency, period);
                }
            }
        }
    },

    updateLastValue(currency, data, currentPrice = null) {
        const valueDisplay = this.settings.valueDisplay;
        if (!valueDisplay) return;
        const lastValue = currentPrice !== null ? currentPrice : (data?.length ? data[data.length - 1] : null);
        if (lastValue === null) return;
        valueDisplay.classList.remove('is-hidden');
        valueDisplay.textContent = this.formatCurrency(lastValue, currency);
    },

    updatePercentageDisplay(percentage) {
        const percentageDisplay = this.settings.percentageDisplay;
        const percentageValueDisplay = this.settings.percentageValueDisplay;

        if (!percentageValueDisplay) return;

        const formattedPercentage = Math.abs(percentage).toFixed(2);
        const isPositive = percentage > 0;
        const isZero = Number(formattedPercentage) === 0;

        percentageValueDisplay.textContent = isZero
            ? '0.00%'
            : `${isPositive ? '+' : ''}${formattedPercentage}%`;

        percentageDisplay.classList.toggle('text-gray', isZero);
        percentageDisplay.classList.toggle('text-green', !isZero && isPositive);
        percentageDisplay.classList.toggle('text-red', !isZero && !isPositive);
    },

    manageCacheSize() {
        const cacheKeys = Object.keys(this.settings.dataCache);
        if (cacheKeys.length > this.CONSTANTS.MAX_CACHE_SIZE) {
            const sortedKeys = cacheKeys.sort((a, b) => 
                this.settings.dataCache[a].timestamp - this.settings.dataCache[b].timestamp
            );
            
            const keysToRemove = sortedKeys.slice(0, cacheKeys.length - this.CONSTANTS.MAX_CACHE_SIZE);
            keysToRemove.forEach(key => delete this.settings.dataCache[key]);
        }
    },

    getPeriodConfig(period) {
        const now = new Date();
        const toDate = now.toISOString();
        let fromDate, granularity, maxDataPoints;
        
        switch(period) {
            case 'daily':
                fromDate = new Date(now);
                fromDate.setHours(0, 0, 0, 0);
                granularity = 'HOURLY';
                maxDataPoints = 24;
                break;
            case 'weekly':
                fromDate = new Date(now);
                fromDate.setDate(fromDate.getDate() - 7);
                granularity = 'DAILY';
                maxDataPoints = 7;
                break;
            case 'monthly':
                fromDate = new Date(now);
                fromDate.setMonth(fromDate.getMonth() - 1);
                granularity = 'DAILY';
                maxDataPoints = 30;
                break;
            case 'yearly':
                fromDate = new Date(now);
                fromDate.setFullYear(fromDate.getFullYear() - 1);
                granularity = 'WEEKLY';
                maxDataPoints = 52;
                break;
            case '5years':
                fromDate = new Date(now);
                fromDate.setFullYear(fromDate.getFullYear() - 5);
                granularity = 'MONTHLY';
                maxDataPoints = 60;
                break;
            default:
                fromDate = new Date(now);
                fromDate.setDate(fromDate.getDate() - 1);
                granularity = 'DAILY';
                maxDataPoints = 24;
        }
        
        return {
            fromDate: fromDate.toISOString(),
            toDate: toDate,
            granularity: granularity,
            maxDataPoints: maxDataPoints
        };
    },

    async getBitcoinHistoryData() {
        const currency = this.settings.selectedCurrency || this.settings.defaultCurrency;
        const period = this.settings.selectedPeriod || 'monthly';
        const cacheKey = `${currency}-${period}`;
        
        if (this.settings.dataCache[cacheKey] && 
            (Date.now() - this.settings.dataCache[cacheKey].timestamp) < this.CONSTANTS.CACHE_DURATION) {
            return this.settings.dataCache[cacheKey].data;
        }
        
        if (this.settings.abortController) {
            this.settings.abortController.abort();
        }
        this.settings.abortController = new AbortController();
        
        // Set up timeout for fetch request
        const timeoutId = setTimeout(() => {
            this.settings.abortController.abort();
        }, 10000);
        
        try {
            const periodConfig = this.getPeriodConfig(period);
            
            const hourlyFromDate = new Date();
            hourlyFromDate.setHours(hourlyFromDate.getHours() - 1);
            
            const res = await fetch('https://api-v3.relai.ch/graphql', {
                method: 'POST',
                headers: {
                    'app-version': '4.0.0',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    query: `
                        query HistoricalBitcoinPrices {
                            currentPrice: bitcoinCurrentPrice(currency:${currency}, priceType:buy),
                            historicalBitcoinPrices(
                                currency: ${currency}
                                fromDate: "${periodConfig.fromDate}"
                                toDate: "${periodConfig.toDate}"
                                granularity: ${periodConfig.granularity}
                                maxDataPoints: ${periodConfig.maxDataPoints}
                            ) {
                                prices {
                                    datetime
                                    bitcoinPrice
                                }
                            }
                            latestHourly: historicalBitcoinPrices(
                                currency: ${currency}
                                fromDate: "${hourlyFromDate.toISOString()}"
                                toDate: "${periodConfig.toDate}"
                                granularity: HOURLY
                                maxDataPoints: 1
                            ) {
                                prices {
                                    datetime
                                    bitcoinPrice
                                }
                            }
                        }
                    `
                }),
                signal: this.settings.abortController.signal
            });

            clearTimeout(timeoutId);
            
            const result = await res.json();
  
            
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            
            if (!result.data?.historicalBitcoinPrices) {
                throw new Error('Invalid API response');
            }
            
            const prices = result.data.historicalBitcoinPrices?.prices;
            const currentPrice = result.data.currentPrice;
            const latestHourlyPrices = result.data.latestHourly?.prices;
            
            if (!prices) {
                return { labels: [], data: [], datetimes: [], rawHistory: [], currentPrice: currentPrice || null };
            }
            
            const pricesArray = Array.isArray(prices) ? prices : Object.values(prices);
            
            if (pricesArray.length === 0) {
                return { labels: [], data: [], datetimes: [], rawHistory: [], currentPrice: currentPrice || null };
            }

            const history = pricesArray.map(price => ({
                datetime: price.datetime,
                value: price.bitcoinPrice
            }));

            history.sort((a, b) => new Date(a.datetime) - new Date(b.datetime));

            if (latestHourlyPrices && latestHourlyPrices.length > 0) {
                const hourlyPrice = latestHourlyPrices[0];
                history.push({
                    datetime: hourlyPrice.datetime,
                    value: hourlyPrice.bitcoinPrice
                });
            }
            
            // Filter data based on period configuration
            const fromDateThreshold = new Date(periodConfig.fromDate);
            const filteredHistory = history.filter(entry => {
                const entryDate = new Date(entry.datetime + (entry.datetime.includes('Z') ? '' : 'Z'));
                return entryDate >= fromDateThreshold;
            });
            

            const labels = filteredHistory.map((entry, index) => {
                const date = new Date(entry.datetime + (entry.datetime.includes('Z') ? '' : 'Z'));
                
                let label = '';
                switch(period) {
                    case 'daily':
                        let hours = date.getUTCHours();
                        let minutes = date.getUTCMinutes();
                        if (minutes < 15) {
                            minutes = 0;
                        } else if (minutes < 45) {
                            minutes = 30;
                        } else {
                            hours = (hours + 1) % 24;
                            minutes = 0;
                        }
                        label = `${hours.toString().padStart(2, '0')}:${minutes === 0 ? '00' : '30'}`;
                        break;
                    
                    case 'weekly':
                        label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
                        break;
                    
                    case 'monthly':
                        label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
                        break;

                    case 'yearly':
                        label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
                        break;
                    
                    case '5years':
                        label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
                        break;
                    
                    default:
                        const defaultHours = date.getUTCHours();
                        const defaultMinutes = date.getUTCMinutes();
                        label = `${defaultHours.toString().padStart(2, '0')}:${defaultMinutes === 0 ? '00' : '30'}`;
                }
                
                return label;
            });

            const data = filteredHistory.map(entry => entry.value);
            const datetimes = filteredHistory.map(entry => entry.datetime);

            const resultData = { labels, data, datetimes, rawHistory: filteredHistory, currentPrice: currentPrice || null };

            this.settings.dataCache[cacheKey] = {
                data: resultData,
                timestamp: Date.now()
            };
            
            this.manageCacheSize();
            return resultData;

            
        } catch (error) {
            clearTimeout(timeoutId);
            
            if (error.name === 'AbortError') {
                return { labels: [], data: [], datetimes: [], rawHistory: [], currentPrice: null };
            }
            
            // Return cached data if available, even if expired
            if (this.settings.dataCache[cacheKey]) {
                console.warn('Using cached data due to error');
                return this.settings.dataCache[cacheKey].data;
            }
            
            return { labels: [], data: [], datetimes: [], rawHistory: [], currentPrice: null }; 
        } 
    },

    createGradient(ctx, height) {
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, 'rgba(249, 136, 0, 0.2)');
        gradient.addColorStop(1, 'rgba(249, 136, 0, 0)');
        return gradient;
    },

    createChartGradient() {
        if (!this.settings.canvas) return null;
        
        const ctx = this.settings.canvas.getContext('2d');
        const canvasHeight = this.settings.chartInstance?.height || 
                            this.settings.canvas.clientHeight || 
                            this.settings.canvas.offsetHeight || 
                            410;
        
        if (this._lastCanvasHeight === canvasHeight && this._cachedGradient) {
            return this._cachedGradient;
        }
        
        this._lastCanvasHeight = canvasHeight;
        this._cachedGradient = this.createGradient(ctx, canvasHeight);
        return this._cachedGradient;
    },

    tooltipShadow() {
        return {
            id: 'tooltipShadow',
            beforeTooltipDraw(chart) {
                const { ctx, tooltip } = chart;
                
                if (tooltip && tooltip.opacity > 0) {
                    ctx.save();
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 16;
                    ctx.shadowBlur = 16;
                    ctx.shadowColor = 'rgba(12, 12, 13, 0.1)';
                }
            },
            afterTooltipDraw(chart) {
                const { ctx, tooltip } = chart;
                
                if (tooltip && tooltip.opacity > 0) {
                    ctx.restore();
                }
            }
        };
    },

    drawCrosshair() {
        return {
            id: 'crosshair',
            afterDatasetsDraw(chart) {
                const { ctx, tooltip, chartArea: { top, bottom, left, right } } = chart;
                
                if (tooltip && tooltip.opacity > 0 && tooltip.dataPoints && tooltip.dataPoints.length > 0) {
                    const activePoint = tooltip.dataPoints[0];
                    const x = activePoint.element.x;
                    const y = activePoint.element.y;

                    ctx.save();
                    
                    ctx.strokeStyle = '#B4B7BD';
                    ctx.lineWidth = 1;
                    ctx.setLineDash([5, 5]);

                    ctx.beginPath();
                    ctx.moveTo(x, top);
                    ctx.lineTo(x, bottom);
                    ctx.stroke();

                    ctx.beginPath();
                    ctx.moveTo(left, y);
                    ctx.lineTo(right, y);
                    ctx.stroke();

                    // Draw circle at intersection
                    ctx.setLineDash([]);
                    ctx.beginPath();
                    ctx.arc(x, y, 8.5, 0, 2 * Math.PI);
                    ctx.fillStyle = '#14217E';
                    ctx.fill();
                    ctx.strokeStyle = '#fff';
                    ctx.lineWidth = 2;
                    ctx.stroke();

                    ctx.restore();
                }
            }
        };
    },

    formatCurrency(value, currency, symbol = false) {
        const num = typeof value === 'number' ? value : 
                    typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : 
                    NaN;

        if (!isFinite(num) || isNaN(num)) return '';

        if (symbol) {
            const locale = currency === 'USD' ? 'en-US' : 'fr-FR';
            return num.toLocaleString(locale, {
                style: 'currency',
                currency: currency
            })
        } else {
            return `${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ').replace('.', ',')} ${currency}`;
        }
    },
    
    getTooltipAlignment(context) {
        const tooltip = context.tooltip;
        if (!tooltip?.dataPoints?.[0]) return 'center';
        
        const chartWidth = context.chart.width;
        const pointX = tooltip.dataPoints[0].element.x;
        
        return pointX > chartWidth * 0.6 ? 'right' : 'left';
    },

    getChartConfig(currency, labels, data, datetimes) {
        const chartObj = this;
        const period = this.settings.selectedPeriod;
        const isMobile = this.isMobile(); 
        const tickLimits = this.getResponsiveTickLimits();
        const dataMin = data.length ? Math.min(...data) : 0;
        const dataMax = data.length ? Math.max(...data) : 1;
        const range = dataMax - dataMin || 1;
        const yMin = Math.max(0, Math.floor((dataMin - range * 0.05) / 1000) * 1000);
        const yMax = Math.ceil((dataMax + range * 0.1) / 1000) * 1000;
        
        return {
            type: 'line',
            data: {
                labels: labels,
                datasets: [{
                    label: '',
                    data: data,
                    borderWidth: 3,
                    borderColor: this.CONSTANTS.ORANGE_COLOR,
                    backgroundColor: this.createChartGradient(),
                    fill: true,
                    tension: 0,
                    pointBackgroundColor: this.CONSTANTS.ACCENT_COLOR,
                    pointBorderWidth: 2,
                    pointBorderColor: '#fff',
                    pointRadius: 0,
                    pointHoverRadius: 0 
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                    padding: { top: 10, right: 10, bottom: 10, left: 10 }
                },
                animation: {
                    duration: 800,
                    easing: 'easeInOutQuart',
                    x: {
                        duration: 0
                    },
                    y: {
                        duration: 800,
                        easing: 'easeInOutQuart'
                    },
                    colors: {
                        duration: 800,
                        easing: 'easeInOutQuart'
                    }
                },
                interaction: {
                    mode: 'index',
                    intersect: false
                },
                plugins: {         
                    legend: {
                        display: false
                    },
                    tooltip: {
                        enabled: true,
                        external: false,
                        backgroundColor: this.CONSTANTS.TOOLTIP_BACKGROUND,
                        titleColor: this.CONSTANTS.TOOLTIP_TITLE_COLOR,
                        bodyColor: this.CONSTANTS.TEXT_COLOR,
                        cornerRadius: 16,
                        displayColors: false,
                        padding: 12,
                        titleFont: {
                            size: 16,
                            lineHeight: '28px',
                            weight: '700',
                            family: this.CONSTANTS.FONT_FAMILY
                        },
                        bodyFont: {
                            size: 12,
                            lineHeight: '16px',
                            weight: '800',
                            family: this.CONSTANTS.FONT_FAMILY
                        },
                        callbacks: {
                            title: function(context) {
                                const value = context[0].parsed?.y;
                                if (value === undefined) return '';
                                return chartObj.formatCurrency(value, currency);
                            },
                            label: function(context) {
                                const dataIndex = context.dataIndex;
                                const datetime = datetimes[dataIndex];

                                if (!datetime) return '';

                                const date = new Date(datetime);
                                const dateStr = date.toLocaleDateString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit', 
                                    year: 'numeric',
                                    timeZone: 'UTC'
                                });
                                
                                // Show hours if time is not midnight
                                const hours = date.getUTCHours();
                                const minutes = date.getUTCMinutes();
                                if (hours !== 0 || minutes !== 0) {
                                    const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
                                    return `${dateStr} ${timeStr}`;
                                }
                                
                                return dateStr;
                            }
                        },
                        position: 'nearest',
                        xAlign: (context) => chartObj.getTooltipAlignment(context),
                        caretSize: 0,
                        caretPadding: 10
                    },
                },
                scales: {
                    x: {
                        display: true,
                        grid: {
                            display: false, 
                            drawBorder: false,
                        },
                        ticks: {
                            includeBounds: true,
                            align: 'start',
                            maxTicksLimit: tickLimits.x,
                            font: {
                                size: 12,
                                family: this.CONSTANTS.FONT_FAMILY,
                                weight: 'bolder'
                            },
                            color: this.CONSTANTS.TEXT_COLOR,
                        },
                        animation: {
                            duration: 0
                        }
                    },
                    y: {
                        beginAtZero: false,
                        min: yMin,
                        max: yMax,
                        position: 'left',
                        offset: false,
                        border: { display: false },
                        grid: {
                            display: true,
                            color: this.CONSTANTS.GRID_COLOR,
                            drawBorder: false
                        },
                        ticks: {
                            maxTicksLimit: tickLimits.y,
                            color: this.CONSTANTS.TEXT_COLOR,
                            mirror: isMobile,
                            z: 1,
                            padding: 0,
                            font: {
                                size: 12,
                                family: this.CONSTANTS.FONT_FAMILY,
                                weight: 'bolder'
                            },
                            callback: (value, index) => {
                                if (isMobile && index === 0) {
                                    return '';
                                }
                                return this.formatCurrency(value, currency, true);
                            }
                        },
                        animation: {
                            duration: 800,
                            easing: 'easeInOutQuart'
                        }
                    }
                }
            }
        };
    },

    async drawPriceHistoryChart() {
        try {
            const { labels, data, datetimes, rawHistory, currentPrice } = await this.getBitcoinHistoryData();
            
            if (!labels.length || !data.length) {
                return;
            }
            
            const currency = this.settings.selectedCurrency || this.settings.defaultCurrency;
            const ctx = this.settings.canvas.getContext('2d');
            const isMobile = this.isMobile();
            this._prevIsMobile = isMobile;
            
            if (this.settings.chartInstance) {
                this.settings.chartInstance.data.labels = labels;
                
                this.settings.chartInstance.data.datasets[0].data = data;
                this.settings.chartInstance.data.datasets[0].backgroundColor = this.createChartGradient();
                this.settings.chartInstance.data.datasets[0].pointRadius = 0;
                this.settings.chartInstance.data.datasets[0].pointHoverRadius = 0;
                
                const dataMin = data.length ? Math.min(...data) : 0;
                const dataMax = data.length ? Math.max(...data) : 1;
                const range = dataMax - dataMin || 1;
                this.settings.chartInstance.options.scales.y.min = Math.max(0, Math.floor((dataMin - range * 0.05) / 1000) * 1000);
                this.settings.chartInstance.options.scales.y.max = Math.ceil((dataMax + range * 0.1) / 1000) * 1000;
                
                if (!this.settings.chartInstance.options.scales.x.animation) {
                    this.settings.chartInstance.options.scales.x.animation = {};
                }
                if (!this.settings.chartInstance.options.scales.y.animation) {
                    this.settings.chartInstance.options.scales.y.animation = {};
                }
                this.settings.chartInstance.options.scales.x.animation.duration = 0;
                this.settings.chartInstance.options.scales.y.animation.duration = 800;
                this.settings.chartInstance.options.scales.y.animation.easing = 'easeInOutQuart';
                
                if (!this.settings.chartInstance.options.animation.x) {
                    this.settings.chartInstance.options.animation.x = {};
                }
                if (!this.settings.chartInstance.options.animation.y) {
                    this.settings.chartInstance.options.animation.y = {};
                }
                this.settings.chartInstance.options.animation.x.duration = 0;
                this.settings.chartInstance.options.animation.y.duration = 800;
                this.settings.chartInstance.options.animation.y.easing = 'easeInOutQuart';
                
                const chartObj = this;
                const currentCurrency = this.settings.selectedCurrency || this.settings.defaultCurrency;
                const currentPeriod = this.settings.selectedPeriod;
                
                this.settings.chartInstance.options.plugins.tooltip.callbacks.title = function(context) {
                    const value = context[0].parsed?.y;
                    if (value === undefined) return '';
                    return chartObj.formatCurrency(value, currentCurrency);
                };
                
                this.settings.chartInstance.options.plugins.tooltip.callbacks.label = function(context) {
                    const dataIndex = context.dataIndex;
                    const cacheKey = `${currentCurrency}-${currentPeriod}`;
                    const datetimesArray = chartObj.settings.dataCache[cacheKey]?.data?.datetimes || datetimes;
                    const datetime = datetimesArray[dataIndex];
                    if (!datetime) return '';
                    const date = new Date(datetime);
                    const dateStr = date.toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric',
                        timeZone: 'UTC'
                    });
                    
                    // Show hours if time is not midnight
                    const hours = date.getUTCHours();
                    const minutes = date.getUTCMinutes();
                    if (hours !== 0 || minutes !== 0) {
                        const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
                        return `${dateStr} ${timeStr}`;
                    }
                    
                    return dateStr;
                };
                
                this.settings.chartInstance.options.scales.y.ticks.callback = (value, index) => {
                    if (isMobile && index === 0) {
                        return '';
                    }
                    return this.formatCurrency(value, currentCurrency, true);
                };

                this.settings.chartInstance.update('active');
                this.updateLastValue(currency, data, currentPrice);
            } else {
                const config = this.getChartConfig(currency, labels, data, datetimes);
                config.plugins = [
                    this.tooltipShadow(),
                    this.drawCrosshair(),
                    this.setValueOnHover()
                ];

                this.settings.chartInstance = new Chart(ctx, config);
                this.updateLastValue(currency, data, currentPrice);
            }

            // If bitcoin-price already fired its update before our listener was ready, apply it now
            if (window.lastBitcoinPriceUpdate) {
                this.handlePriceUpdate(window.lastBitcoinPriceUpdate);
            }

        } catch (error) {
        }
    },

    listenForPriceUpdates() {
        const priceUpdateHandler = (event) => {
            this.handlePriceUpdate(event.detail);
        };
        
        window.addEventListener('bitcoinPriceUpdate', priceUpdateHandler);
        this.settings.eventListeners.push({
            element: window,
            event: 'bitcoinPriceUpdate',
            handler: priceUpdateHandler
        });
    },

    handlePriceUpdate(updateData) {
        if (!this.settings.chartInstance || !updateData?.prices) return;
        
        const currency = this.settings.selectedCurrency || this.settings.defaultCurrency;
        const priceData = updateData.prices[currency];
        
        if (!priceData) return;

        // Always update the displayed value on every price-block interval
        const isHoveringChart = this.settings.chartInstance.tooltip?.opacity > 0;
        if (this.settings.valueDisplay && !isHoveringChart) {
            this.settings.valueDisplay.classList.remove('is-hidden');
            this.settings.valueDisplay.textContent = this.formatCurrency(priceData.value, currency);
        }

        const period = this.settings.selectedPeriod;
        const cacheKey = `${currency}-${period}`;
        const cachedData = this.settings.dataCache[cacheKey];

        if (!cachedData?.data) return;
        
        const labels = this.settings.chartInstance.data.labels;
        const data = this.settings.chartInstance.data.datasets[0].data;
        
        // Check if the new value is the same as the last value
        const lastValue = data.length > 0 ? data[data.length - 1] : null;
        if (lastValue !== null && priceData.value === lastValue) {
            return; // Don't append if value is the same
        }
        
        // Add new data point
        const date = new Date(priceData.datetime);
        let label = '';
        
        switch(period) {
            case 'daily':
                let hours = date.getUTCHours();
                let minutes = date.getUTCMinutes();
                if (minutes < 15) {
                    minutes = 0;
                } else if (minutes < 45) {
                    minutes = 30;
                } else {
                    hours = (hours + 1) % 24;
                    minutes = 0;
                }
                label = `${hours.toString().padStart(2, '0')}:${minutes === 0 ? '00' : '30'}`;
                break;
            
            case 'weekly':
                label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
                break;
            
            case 'monthly':
                label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
                break;

            case 'yearly':
                label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
                break;
            
            case '5years':
                label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
                break;
        }
        
        // Update cache with new data point
        cachedData.data.rawHistory.push({
            datetime: priceData.datetime,
            value: priceData.value
        });
        
        // Update datetimes array (used by tooltips)
        if (!cachedData.data.datetimes) {
            cachedData.data.datetimes = [];
        }
        cachedData.data.datetimes.push(priceData.datetime);
        
        // Add to chart
        labels.push(label);
        data.push(priceData.value);
        
        // Recalculate scales
        const dataMin = data.length ? Math.min(...data) : 0;
        const dataMax = data.length ? Math.max(...data) : 1;
        const range = dataMax - dataMin || 1;
        this.settings.chartInstance.options.scales.y.min = Math.max(0, Math.floor((dataMin - range * 0.05) / 1000) * 1000);
        this.settings.chartInstance.options.scales.y.max = Math.ceil((dataMax + range * 0.1) / 1000) * 1000;
        
        // Update chart
        this.settings.chartInstance.update('active');
    }
};

window.addEventListener('DOMContentLoaded', () => chart.init());
window.addEventListener('beforeunload', () => chart.destroy());
